import moment from "moment"
import { toDecimal } from "src/utils/toDecimal"
import { defaultRubros, RUBROS_TYPE } from "src/utils/Constants"

const periodoList = {
  DIARIO: "diario",
  SEMANAL: "semanal",
  MENSUAL: "mensual",
}

const periodoValor = {
  [periodoList.DIARIO]: 365,
  [periodoList.SEMANAL]: 52,
  [periodoList.MENSUAL]: 12,
}

const periodoUnidadMoment = {
  [periodoList.DIARIO]: "days",
  [periodoList.SEMANAL]: "weeks",
  [periodoList.MENSUAL]: "months",
}

export const tipoInteresList = {
  FIJO: "fijo",
  VARIABLE: "variable",
}

export const formatTipoInteres = value =>
  `${value || ""}`.toLowerCase().trim() === tipoInteresList.VARIABLE
    ? tipoInteresList.VARIABLE
    : tipoInteresList.FIJO

const toTasaPrecisa = value => {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? Number(numberValue.toFixed(10)) : 0
}

const toJsDate = value => {
  if (!value) return null
  if (typeof value.toDate === "function") return value.toDate()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const toPositiveNumber = value => {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : 0
}

export const formatPeriodoInteres = value => {
  const periodo = `${value || ""}`.toLowerCase().trim()
  if (periodo === periodoList.DIARIO) {
    return periodoList.DIARIO
  }
  if (periodo === periodoList.SEMANAL) {
    return periodoList.SEMANAL
  }
  return periodoList.MENSUAL
}

const getFactorAnual = periodo => periodoValor[formatPeriodoInteres(periodo)]

const convertFromAnual = (porcentajeAnual, periodo) =>
  toTasaPrecisa(toPositiveNumber(porcentajeAnual) / getFactorAnual(periodo))

const resolveTasaConfigPorPeriodo = (configIntereses, periodo) => {
  const normalized = formatPeriodoInteres(periodo)
  if (formatPeriodoInteres(configIntereses?.periodo) !== normalized) return 0
  return toPositiveNumber(configIntereses?.porcentaje)
}

const resolveTasaTramoPeriodo = (tramo, periodo) => {
  const normalized = formatPeriodoInteres(periodo)
  if (formatPeriodoInteres(tramo?.periodo) !== normalized) return 0
  return toPositiveNumber(tramo?.porcentaje)
}

const resolveTasaPorPeriodo = (tramo, configIntereses) => {
  const periodo = formatPeriodoInteres(
    tramo?.periodo || configIntereses?.periodo
  )

  const tasaDirecta =
    resolveTasaTramoPeriodo(tramo, periodo) ||
    resolveTasaConfigPorPeriodo(configIntereses, periodo)
  if (tasaDirecta > 0) {
    return {
      periodo,
      porcentaje: toTasaPrecisa(tasaDirecta),
    }
  }

  const tasaAnual = toPositiveNumber(tramo?.porcentajeAnual)

  if (tasaAnual > 0) {
    return {
      periodo,
      porcentaje: convertFromAnual(tasaAnual, periodo),
    }
  }

  return {
    periodo,
    porcentaje: 0,
  }
}

// copia la configuración activa al map Interes de la CxC base
export const buildInteresSnapshot = configIntereses => {
  const { periodo, porcentaje } = resolveTasaPorPeriodo({}, configIntereses)
  const tasaAnual = toTasaPrecisa(porcentaje * getFactorAnual(periodo))
  return {
    TipoInteres: formatTipoInteres(configIntereses?.tipo),
    PeriodoInteres: periodo,
    TasaInteresAnual: tasaAnual,
  }
}

export const hasInteresMoraConfig = configIntereses => {
  const periodo = formatPeriodoInteres(configIntereses?.periodo)
  return resolveTasaConfigPorPeriodo(configIntereses, periodo) > 0
}

export const isInteresMoraActivo = (modulos, configIntereses) =>
  modulos?.InteresMora === true && hasInteresMoraConfig(configIntereses)

export const resolveHistorialTasas = (
  configIntereses,
  fechaInicio = new Date(0)
) => {
  if (
    Array.isArray(configIntereses?.Historial) &&
    configIntereses.Historial.length > 0
  ) {
    const historialNormalizado = configIntereses.Historial.map(tramo => {
      const { periodo, porcentaje } = resolveTasaPorPeriodo(
        tramo,
        configIntereses
      )
      const porcentajeAnualCalculado = toTasaPrecisa(
        porcentaje * getFactorAnual(periodo)
      )
      const porcentajeAnual =
        toPositiveNumber(tramo?.porcentajeAnual) || porcentajeAnualCalculado

      return {
        ...tramo,
        periodo,
        porcentaje,
        porcentajeAnual,
      }
    })

    if (historialNormalizado.some(tramo => Number(tramo?.porcentaje) > 0)) {
      return historialNormalizado
    }
  }

  const { periodo, porcentaje } = resolveTasaPorPeriodo({}, configIntereses)
  if (porcentaje > 0) {
    return [
      {
        periodo,
        porcentaje,
        porcentajeAnual: toTasaPrecisa(porcentaje * getFactorAnual(periodo)),
        vigenteDesde: fechaInicio,
        vigenteHasta: null,
      },
    ]
  }
  return []
}

const normalizarHistorial = (historial = []) =>
  [...historial]
    .filter(tramo => tramo && tramo.vigenteDesde)
    .sort((a, b) => toJsDate(a.vigenteDesde) - toJsDate(b.vigenteDesde))
    .map((tramo, index, tramosOrdenados) => {
      const siguiente = tramosOrdenados[index + 1]
      const vigenteHastaCalculada =
        tramo.vigenteHasta == null && siguiente
          ? siguiente.vigenteDesde
          : tramo.vigenteHasta

      return {
        ...tramo,
        periodoInteres: formatPeriodoInteres(tramo?.periodo),
        vigenteHasta: vigenteHastaCalculada ?? null,
      }
    })

const diffByPeriodo = (desde, hasta, periodo) => {
  const normalized = formatPeriodoInteres(periodo)
  if (normalized === periodoList.DIARIO) {
    return moment(hasta).diff(moment(desde), "days")
  }
  if (normalized === periodoList.SEMANAL) {
    return moment(hasta).diff(moment(desde), "weeks")
  }
  return moment(hasta).diff(moment(desde), "months")
}

const getSubTramos = (historial, desde, hasta) => {
  const tramos = normalizarHistorial(historial)
  if (tramos.length === 0) return []

  const inicio = moment(toJsDate(desde))
  const fin = moment(toJsDate(hasta))
  if (!inicio.isValid() || !fin.isValid() || !fin.isAfter(inicio)) return []

  const subTramos = []
  let cursor = inicio

  tramos.forEach(tramo => {
    const tramoDesde = moment.max(moment(toJsDate(tramo.vigenteDesde)), inicio)
    const tramoHasta = tramo.vigenteHasta
      ? moment.min(moment(toJsDate(tramo.vigenteHasta)), fin)
      : fin

    if (tramoHasta.isAfter(cursor) && tramoHasta.isAfter(tramoDesde)) {
      const desdeEfectivo = moment.max(tramoDesde, cursor)
      if (tramoHasta.isAfter(desdeEfectivo)) {
        subTramos.push({
          porcentaje: tramo.porcentaje,
          periodoInteres: tramo.periodoInteres,
          desde: desdeEfectivo.toDate(),
          hasta: tramoHasta.toDate(),
        })
        cursor = tramoHasta
      }
    }
  })

  return subTramos
}

export const calcularInteresEntreFechas = (
  capitalBase,
  historialTasas,
  desde,
  hasta
) => {
  const capital = Number(capitalBase) || 0
  if (capital <= 0) return 0

  const subTramos = getSubTramos(historialTasas, desde, hasta)
  const totalInteres = subTramos.reduce((acc, tramo, index) => {
    const esTramoVigente = index === subTramos.length - 1
    let periodosACobrar = diffByPeriodo(
      tramo.desde,
      tramo.hasta,
      tramo.periodoInteres
    )
    if (esTramoVigente) {
      periodosACobrar += 1
    }
    if (periodosACobrar <= 0) return acc
    const tasaPorPeriodo = (Number(tramo.porcentaje) || 0) / 100
    return acc + capital * tasaPorPeriodo * periodosACobrar
  }, 0)

  return toDecimal(totalInteres)
}

const calcularIndicePeriodoMora = (fechaInicioMora, fecha, periodo) => {
  const inicio = moment(toJsDate(fechaInicioMora))
  const objetivo = moment(toJsDate(fecha))
  if (!inicio.isValid() || !objetivo.isValid() || objetivo.isBefore(inicio)) {
    return 0
  }
  return diffByPeriodo(inicio.toDate(), objetivo.toDate(), periodo) + 1
}

// se guarda el periodo de interes en history
const construirHistorialTasas = historial =>
  normalizarHistorial(historial).map(tramo => ({
    desde: moment(toJsDate(tramo.vigenteDesde)).valueOf(),
    porcentajeAnual: Number(tramo.porcentajeAnual) || 0,
    periodoInteres: formatPeriodoInteres(tramo.periodoInteres),
  }))

// se obtiene el tramo vigente en la fecha dada
const obtenerTramoEnFecha = (tabla, fechaMs) => {
  if (tabla.length === 0) return null
  let resuelto = tabla[0]
  for (let i = 1; i < tabla.length; i += 1) {
    if (tabla[i].desde > fechaMs) break
    resuelto = tabla[i]
  }
  return resuelto
}

export const calcularInteresActual = (
  cxcInteres,
  historialTasas,
  fechaCorte = new Date()
) => {
  const causadoCongelado = Number(cxcInteres?.InteresAcumuladoCausado) || 0
  const pagado = Number(cxcInteres?.InteresAcumuladoPagado) || 0
  const ultimoIndiceFacturado = Number(cxcInteres?.UltimoMesFacturado) || 0
  const capitalBase = Number(cxcInteres?.CapitalBaseRemanente) || 0
  const fechaInicioMora = cxcInteres?.FechaInicioMora
  const tabla = construirHistorialTasas(historialTasas)
  const inicioMoraMs = fechaInicioMora
    ? moment(toJsDate(fechaInicioMora)).valueOf()
    : moment(toJsDate(fechaCorte)).valueOf()
  const tramoInicial = obtenerTramoEnFecha(tabla, inicioMoraMs)
  const periodoInteres = cxcInteres?.PeriodoInteres
    ? formatPeriodoInteres(cxcInteres.PeriodoInteres)
    : tramoInicial?.periodoInteres || periodoList.MENSUAL
  const factorAnual = getFactorAnual(periodoInteres)
  const indiceCalculado = fechaInicioMora
    ? calcularIndicePeriodoMora(fechaInicioMora, fechaCorte, periodoInteres)
    : ultimoIndiceFacturado
  // se evita doble cobro si la cxc ya tiene un corte futuro al corte
  const mesIndexActual = Math.max(indiceCalculado, ultimoIndiceFacturado)
  const periodosNuevos = mesIndexActual - ultimoIndiceFacturado

  // variable, se suma la tasa de cada periodo
  let tasaAcumulada = 0
  if (periodosNuevos > 0) {
    if (formatTipoInteres(cxcInteres?.TipoInteres) === tipoInteresList.FIJO) {
      const tasaAnualFija =
        toPositiveNumber(cxcInteres?.TasaInteresAnual) ||
        tramoInicial?.porcentajeAnual ||
        0
      tasaAcumulada = (tasaAnualFija / factorAnual) * periodosNuevos
    } else {
      const inicioMora = moment(toJsDate(fechaInicioMora))
      const unidad = periodoUnidadMoment[periodoInteres]
      for (let k = ultimoIndiceFacturado + 1; k <= mesIndexActual; k += 1) {
        const inicioPeriodoMs = inicioMora
          .clone()
          .add(k - 1, unidad)
          .valueOf()
        const tramo = obtenerTramoEnFecha(tabla, inicioPeriodoMs)
        tasaAcumulada += (tramo?.porcentajeAnual || 0) / factorAnual
      }
    }
  }

  const interesNuevo = capitalBase * (tasaAcumulada / 100)

  const interesCausadoTotal = toDecimal(causadoCongelado + interesNuevo)
  const interesPendiente = toDecimal(Math.max(interesCausadoTotal - pagado, 0))

  return {
    interesCausadoTotal,
    interesPendiente,
    mesIndexActual,
    periodoInteres,
  }
}

export const imputarPago = (
  cxcBase,
  cxcInteres,
  historialTasas,
  montoPago,
  fechaPago = new Date()
) => {
  const monto = Number(montoPago) || 0
  const capitalPendienteActual =
    Number(cxcBase?.CapitalPendiente ?? cxcBase?.Valor) || 0

  if (!cxcInteres) {
    const capitalPagado = Math.min(monto, capitalPendienteActual)
    return {
      interesPagado: 0,
      capitalPagado,
      capitalPendiente: toDecimal(capitalPendienteActual - capitalPagado),
      cxcInteresUpdate: null,
    }
  }

  const { interesCausadoTotal, mesIndexActual, periodoInteres } =
    calcularInteresActual(cxcInteres, historialTasas, fechaPago)
  const pagadoPrevio = Number(cxcInteres?.InteresAcumuladoPagado) || 0
  const interesPendiente = toDecimal(
    Math.max(interesCausadoTotal - pagadoPrevio, 0)
  )

  const interesPagado = toDecimal(Math.min(monto, interesPendiente))
  const restanteParaCapital = toDecimal(monto - interesPagado)
  const capitalPagado = toDecimal(
    Math.min(restanteParaCapital, capitalPendienteActual)
  )

  const nuevoInteresPagadoAcumulado = toDecimal(pagadoPrevio + interesPagado)
  const nuevoInteresPendiente = toDecimal(
    interesCausadoTotal - nuevoInteresPagadoAcumulado
  )
  const nuevoCapitalPendiente = toDecimal(
    capitalPendienteActual - capitalPagado
  )

  let estadoPago = "Pendiente"
  if (nuevoInteresPendiente <= 0 && nuevoCapitalPendiente <= 0) {
    estadoPago = "Pagado"
  } else if (nuevoInteresPagadoAcumulado > 0 || capitalPagado > 0) {
    estadoPago = "PagadoParcial"
  }

  return {
    interesPagado,
    capitalPagado,
    capitalPendiente: nuevoCapitalPendiente,
    cxcInteresUpdate: {
      FechaUltimoCorte: fechaPago,
      UltimoMesFacturado: mesIndexActual,
      PeriodoInteres: periodoInteres,
      InteresAcumuladoCausado: interesCausadoTotal,
      InteresAcumuladoPagado: nuevoInteresPagadoAcumulado,
      CapitalBaseRemanente: nuevoCapitalPendiente,
      EstadoPago: estadoPago,
    },
  }
}

export const interesID = "interesInterno-"
export const getInteresCuentaId = baseId => `${interesID}${baseId}`
export const isInteresEmbebidoId = id =>
  typeof id === "string" && id.startsWith(interesID)
export const getBaseIdFromInteresId = id => {
  if (typeof id !== "string") return id
  if (id.startsWith(interesID)) return id.slice(interesID.length)
  return id
}

export const getCuentaDocId = cuenta =>
  cuenta?.InteresEmbebido || isInteresEmbebidoId(cuenta?.id)
    ? cuenta?.CuentaBaseId || getBaseIdFromInteresId(cuenta?.id)
    : cuenta?.id

export const buildCuentaInteresDesdeBase = (base, rubrosAutomaticos = []) => {
  const interes = base?.Interes
  if (!base?.id || !interes || typeof interes !== "object") return null
  const rubroInteres =
    (Array.isArray(rubrosAutomaticos) ? rubrosAutomaticos : []).find(
      item =>
        item?.TipoDePago === RUBROS_TYPE.interesMora ||
        item?.TipoDePago === "Interés por mora"
    ) || defaultRubros.get(RUBROS_TYPE.interesMora)

  return {
    ...interes,
    id: getInteresCuentaId(base.id),
    InteresEmbebido: true,
    TipoCuenta: "Interes",
    CuentaBaseId: base.id,
    Nombre: interes.Nombre || `Interés por mora - ${base.Nombre || ""}`.trim(),
    Descripcion:
      interes.Descripcion || `Interés por mora de ${base.Nombre || ""}`.trim(),
    CasaUsuario: base.CasaUsuario,
    UsuarioId: base.UsuarioId,
    NombreUsuario: base.NombreUsuario,
    Rubro: rubroInteres.Rubro,
    SubRubro: rubroInteres.SubRubro,
    FechaRegistro: base.FechaRegistro,
    FechaLimite: interes.FechaInicioMora || base.FechaLimite,
  }
}

export const toInteresUpdate = update =>
  Object.entries(update || {}).reduce(
    (acc, [key, value]) => ({ ...acc, [`Interes.${key}`]: value }),
    {}
  )

export const enriquecerCuentasConInteres = (
  items = [],
  {
    interesMoraActivo,
    interesConfig,
    rubrosAutomaticos = [],
    fechaCorte = new Date(),
  } = {}
) => {
  const cuentasCompatibles = items.filter(
    item =>
      item?.TipoCuenta !== "Interes" ||
      item?.InteresEmbebido ||
      isInteresEmbebidoId(item?.id)
  )
  const existentes = new Set(cuentasCompatibles.map(item => item?.id))
  return cuentasCompatibles
    .flatMap(item => {
      const interes = buildCuentaInteresDesdeBase(item, rubrosAutomaticos)
      return interes && !existentes.has(interes.id) ? [item, interes] : [item]
    })
    .filter(item => interesMoraActivo || item?.TipoCuenta !== "Interes")
    .map(item => {
      if (item?.TipoCuenta !== "Interes") return item
      const historial = resolveHistorialTasas(
        interesConfig,
        item.FechaInicioMora
      )
      const { interesCausadoTotal, interesPendiente } = calcularInteresActual(
        item,
        historial,
        fechaCorte
      )
      return {
        ...item,
        Valor: interesPendiente,
        ValorOriginal: interesCausadoTotal,
      }
    })
    .filter(
      item => item?.TipoCuenta !== "Interes" || Number(item?.Valor || 0) > 0
    )
}
